import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal, Field } from '../ui/Modal';
import { Icon } from '../ui/Icon';
import { api, ApiError } from '../../lib/api';
import { toast } from '../../stores/toast.store';
import { SALStatus } from '@masamune/shared';
import { fmtEur } from '../../lib/utils';

interface MilestoneOption { id: string; label: string; dueDate: string }

export function NewSALModal({ onClose, projectId }: { onClose: () => void; projectId?: string }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    projectId: projectId ?? '',
    milestoneId: '',
    label: '',
    amount: 0,
    dueDate: '',
    status: SALStatus.PREVISTA as string,
    notes: '',
    generateInvoiceDraft: false,
  });
  const [vat, setVat] = useState(22);

  const { data: project } = useQuery({
    queryKey: ['project-milestones', form.projectId],
    queryFn: () => api.get<{ milestones: MilestoneOption[] }>(`/projects/${form.projectId}`),
    enabled: !!form.projectId,
  });
  const { data: projects } = useQuery({
    queryKey: ['projects-lite'],
    queryFn: () => api.get<{ items: Array<{ id: string; code: string; name: string }> }>('/projects?pageSize=200'),
    enabled: !projectId,
  });

  const create = useMutation({
    mutationFn: () => api.post(`/projects/${form.projectId}/sal`, {
      milestoneId: form.milestoneId || null,
      label: form.label,
      amount: Number(form.amount),
      dueDate: form.dueDate || null,
      status: form.status,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project', form.projectId] });
      toast.ok('SAL creato', form.label);
      onClose();
    },
    onError: (err) => toast.alert('Errore creazione SAL', err instanceof ApiError ? err.message : 'Riprova'),
  });

  const ivaAmt = +(Number(form.amount) * vat / 100).toFixed(2);
  const total = +(Number(form.amount) + ivaAmt).toFixed(2);

  return (
    <Modal
      title="Nuovo SAL"
      kicker="Budget · Stato Avanzamento Lavori"
      onClose={onClose}
      size="lg"
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Annulla</button>
          <div className="spacer" />
          <span className="mute mono text-[12px]">Totale: € {fmtEur(total, true)}</span>
          <button className="btn btn-primary" onClick={() => create.mutate()} disabled={!form.projectId || !form.label || !form.amount || create.isPending}>
            Crea SAL <Icon name="arrowRight" size={12} />
          </button>
        </>
      }
    >
      <div className="form-grid">
        {!projectId && (
          <Field label="Progetto" required span={12}>
            <select className="inp" value={form.projectId} onChange={(e) => setForm({ ...form, projectId: e.target.value })}>
              <option value="">— seleziona —</option>
              {(projects?.items ?? []).map((p) => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}
            </select>
          </Field>
        )}
        <Field label="Titolo SAL" required span={8}>
          <input className="inp" placeholder="es. SAL #3 — Build milestone 25%" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
        </Field>
        <Field label="Milestone collegata" span={4}>
          <select className="inp" value={form.milestoneId} onChange={(e) => setForm({ ...form, milestoneId: e.target.value })}>
            <option value="">—</option>
            {(project?.milestones ?? []).map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
        </Field>
        <Field label="Imponibile (€)" required span={4}>
          <input className="inp mono text-right" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })} />
        </Field>
        <Field label="IVA (%)" span={2}>
          <input className="inp mono text-right" type="number" value={vat} onChange={(e) => setVat(Number(e.target.value))} />
        </Field>
        <Field label="Totale" span={3}>
          <input className="inp mono text-right" value={fmtEur(total, true)} disabled />
        </Field>
        <Field label="Scadenza" span={3}>
          <input className="inp mono" type="date" value={form.dueDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} />
        </Field>
        <Field label="Stato" span={12}>
          <div className="seg-control">
            {[SALStatus.PREVISTA, SALStatus.EMESSA, SALStatus.PAGATA].map((s) => (
              <button key={s} aria-pressed={form.status === s} onClick={() => setForm({ ...form, status: s })}>{s}</button>
            ))}
          </div>
        </Field>
        <Field label="Note" span={12}>
          <textarea className="inp" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </Field>
        <Field span={12}>
          <label className="flex items-center gap-2 text-[13px]">
            <input type="checkbox" checked={form.generateInvoiceDraft} onChange={(e) => setForm({ ...form, generateInvoiceDraft: e.target.checked })} />
            Genera bozza fattura dal SAL (solo se stato = Emesso)
          </label>
        </Field>
      </div>
    </Modal>
  );
}
